import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import ScrollReveal from "@/components/ScrollReveal";
import { Star, Facebook, ThumbsUp, Quote, BadgeCheck } from "lucide-react";
import { Helmet } from "react-helmet-async";

interface Review {
  id: number;
  name: string;
  date: string;
  isoDate: string;
  text: string;
}

const reviews: Review[] = [
  {
    id: 0,
    name: "Matt Atkinson",
    date: "Apr 9, 2026",
    isoDate: "2026-04-09",
    text: "Perfect experience! Prompt, fair price, expert work done fast. I'll be hiring you again for stairs and maybe other floors in the house too.",
  },
  {
    id: 1,
    name: "Cody Reynolds",
    date: "Feb 3, 2026",
    isoDate: "2026-02-03",
    text: "Our hardwood floors look like new again! He was able to give a fast and accurate quote for both price and estimated timeline. The workmanship is stellar all around and we appreciated the regular updates of progress throughout the process as the work got done. We are very happy with the results!",
  },
  {
    id: 2,
    name: "Nikki Bires-Jude",
    date: "Jan 13, 2026",
    isoDate: "2026-01-13",
    text: "The new wood floors completely transformed our cabin. Beautiful craftsmanship, clean work, and attention to detail that truly shows. Couldn't be happier with the result! Highly recommend Big Bear Pro Flooring. Very much appreciated!! 🥰",
  },
  {
    id: 3,
    name: "Christine Trujillo",
    date: "Aug 9, 2025",
    isoDate: "2025-08-09",
    text: "He did an amazing job on my new lvp flooring!! Highly recommend!",
  },
  {
    id: 4,
    name: "Joey Garcia",
    date: "Aug 13, 2025",
    isoDate: "2025-08-13",
    text: "Fast, efficient, and affordable! Jerry did an amazing job bringing my project to life and I couldn't be happier.",
  },
  {
    id: 5,
    name: "Jessica Garcia",
    date: "Jun 29, 2025",
    isoDate: "2025-06-29",
    text: "Big bears pro flooring is amazing and efficient. Needed new flooring in my camper they came out perfect exactly what I wanted. 100% recommend",
  },
  {
    id: 6,
    name: "Shauna Williams",
    date: "Jun 2, 2025",
    isoDate: "2025-06-02",
    text: "He did an amazing job on our floors and worked very hard! He really cares and takes his time to pay attention to details. I would highly recommend!",
  },
  {
    id: 7,
    name: "Darnell Adam",
    date: "Jun 2, 2025",
    isoDate: "2025-06-02",
    text: "Jerry gets the job done amazing price amazing work thank you very much",
  },
];

const FEATURED_ID = 1; // Cody Reynolds

const Reviews = () => {
  const featured = reviews.find((r) => r.id === FEATURED_ID)!;
  const rest = reviews.filter((r) => r.id !== FEATURED_ID);

  const reviewSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://bigbearsfloors.com/#business",
    name: "Big Bear's Pro Floors",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5.0",
      reviewCount: reviews.length,
      bestRating: "5",
      worstRating: "1",
    },
    review: reviews.map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.name },
      datePublished: r.isoDate,
      reviewRating: {
        "@type": "Rating",
        ratingValue: "5",
        bestRating: "5",
        worstRating: "1",
      },
      reviewBody: r.text,
    })),
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Customer Reviews | Big Bear's Pro Floors — 5★ Rated</title>
        <meta
          name="description"
          content="Read 8 verified 5-star Facebook reviews from happy Big Bear's Pro Floors customers. 100% recommend our hardwood, LVP, refinishing, and stair work."
        />
        <link rel="canonical" href="https://bigbearsfloors.com/reviews" />
        <script type="application/ld+json">{JSON.stringify(reviewSchema)}</script>
      </Helmet>

      <Header />
      <main className="pt-20">
        {/* Hero / Rating Summary */}
        <section className="bg-gradient-to-b from-primary to-primary/90 py-20">
          <div className="container mx-auto px-4">
            <ScrollReveal>
              <div className="text-center max-w-3xl mx-auto">
                <h1 className="text-4xl md:text-5xl font-heading font-bold text-primary-foreground mb-4">
                  What Our Customers Say
                </h1>
                <p className="text-primary-foreground/80 text-lg mb-10">
                  Real reviews from real Big Bear's Pro Floors customers.
                </p>

                {/* Rating Stat Block */}
                <div className="bg-card/95 backdrop-blur rounded-2xl shadow-xl p-8 md:p-10 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4 items-center">
                  <div className="text-center md:border-r md:border-border">
                    <div className="text-5xl md:text-6xl font-heading font-bold text-foreground leading-none">
                      5.0
                    </div>
                    <div className="flex justify-center gap-1 my-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-accent fill-accent" />
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground font-medium">
                      Average Rating
                    </p>
                  </div>

                  <div className="text-center md:border-r md:border-border">
                    <div className="text-5xl md:text-6xl font-heading font-bold text-accent leading-none">
                      {reviews.length}
                    </div>
                    <p className="text-sm text-muted-foreground font-medium mt-3">
                      Verified Reviews
                    </p>
                  </div>

                  <div className="text-center">
                    <div className="text-5xl md:text-6xl font-heading font-bold text-foreground leading-none">
                      100%
                    </div>
                    <p className="text-sm text-muted-foreground font-medium mt-3">
                      Recommend
                    </p>
                  </div>
                </div>

                <a
                  href="https://www.facebook.com/profile.php?id=61574755531186&sk=reviews"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-accent-foreground px-6 py-3 rounded-lg font-heading font-bold transition-colors mt-8"
                >
                  <Facebook className="w-5 h-5" />
                  See Reviews on Facebook
                </a>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Featured Review */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <ScrollReveal>
                <div className="relative bg-gradient-to-br from-accent/10 via-card to-card border-2 border-accent/30 rounded-2xl p-8 md:p-12 shadow-lg">
                  <Quote className="absolute top-6 left-6 w-12 h-12 text-accent/30" />
                  <div className="absolute top-6 right-6 inline-flex items-center gap-1 bg-accent text-accent-foreground text-xs font-heading font-bold uppercase tracking-wide px-3 py-1 rounded-full">
                    <Star className="w-3 h-3 fill-current" /> Featured
                  </div>

                  <div className="pt-12 md:pt-8 md:pl-16">
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-accent fill-accent" />
                      ))}
                    </div>
                    <blockquote className="text-xl md:text-2xl font-heading text-foreground leading-relaxed italic mb-6">
                      "{featured.text}"
                    </blockquote>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center shrink-0">
                        <span className="font-heading font-bold text-primary-foreground text-lg">
                          {featured.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-heading font-bold text-foreground">
                          {featured.name}
                        </p>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <BadgeCheck className="w-4 h-4 text-accent" />
                          Verified Facebook Review · {featured.date}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* Reviews List */}
        <section className="pb-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <ScrollReveal>
                <div className="flex items-center justify-center gap-3 mb-10">
                  <Facebook className="w-7 h-7 text-accent" />
                  <span className="font-heading font-bold text-2xl">
                    More Facebook Reviews
                  </span>
                </div>
              </ScrollReveal>

              <div className="space-y-6">
                {rest.map((review, index) => (
                  <ScrollReveal key={review.id} delay={index * 0.05}>
                    <div className="bg-card border border-border rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3 gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center shrink-0">
                            <span className="font-heading font-bold text-primary-foreground text-lg">
                              {review.name.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="font-heading font-bold text-foreground">
                              {review.name}
                            </p>
                            <p className="text-sm text-muted-foreground flex items-center gap-2">
                              <ThumbsUp className="w-4 h-4 text-accent" />
                              <span>recommends Big Bear's Pro Floors</span>
                            </p>
                          </div>
                        </div>
                        <span className="text-sm text-muted-foreground whitespace-nowrap">
                          {review.date}
                        </span>
                      </div>
                      <div className="flex gap-0.5 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-accent fill-accent" />
                        ))}
                      </div>
                      <p className="text-foreground leading-relaxed">{review.text}</p>
                    </div>
                  </ScrollReveal>
                ))}
              </div>

              {/* Trust indicators */}
              <ScrollReveal>
                <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                  <div className="p-6 bg-muted rounded-lg">
                    <div className="text-4xl font-heading font-bold text-accent mb-2">
                      100%
                    </div>
                    <p className="text-muted-foreground">Customer Satisfaction</p>
                  </div>
                  <div className="p-6 bg-muted rounded-lg">
                    <div className="text-4xl font-heading font-bold text-accent mb-2">
                      Local
                    </div>
                    <p className="text-muted-foreground">Owner-Operated</p>
                  </div>
                  <div className="p-6 bg-muted rounded-lg">
                    <div className="text-4xl font-heading font-bold text-accent mb-2">
                      Quality
                    </div>
                    <p className="text-muted-foreground">Guaranteed Work</p>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default Reviews;
